import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";import { UploadCloud, X, Coins, Loader2, ScanText, FileText } from "lucide-react";

export default function Evaluate() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const fileInput = useRef(null);

  const [examType, setExamType] = useState("upsc");
  const [subject, setSubject] = useState("");
  const [maxMarks, setMaxMarks] = useState("10");
  const [wordLimit, setWordLimit] = useState("150");
  const [language, setLanguage] = useState("english");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [cfg, setCfg] = useState({ pages_per_credit: 5, max_pages: 10 });

  useEffect(() => {
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const imagesOnly = files.length > 0 && files.every((f) => !f.isPdf);
  const estCredits = files.length > 0 ? Math.max(1, Math.ceil(files.length / cfg.pages_per_credit)) : 1;

  const onFiles = (list) => {
    const arr = Array.from(list || []);
    const accepted = [];
    for (const f of arr) {
      const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
      if (!f.type.startsWith("image/") && !isPdf) {
        toast.error(`${f.name}: unsupported. Upload an image or PDF.`);
        continue;
      }
      accepted.push({ file: f, isPdf, name: f.name, preview: isPdf ? null : URL.createObjectURL(f) });
    }
    setFiles((prev) => {
      let combined = [...prev, ...accepted];
      const hasPdf = combined.some((f) => f.isPdf);
      if (!hasPdf && combined.length > cfg.max_pages) {
        toast.error(`You can upload up to ${cfg.max_pages} pages. Extra pages were not added.`);
        combined = combined.slice(0, cfg.max_pages);
      }
      return combined;
    });
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return toast.error("Please upload at least one answer page.");
    if (imagesOnly) {
      if (files.length > cfg.max_pages) return toast.error(`Maximum ${cfg.max_pages} pages per evaluation. Please split it.`);
      if ((user?.credits ?? 0) < estCredits) {
        toast.error(`This ${files.length}-page answer needs ${estCredits} credit${estCredits > 1 ? "s" : ""}.`);
        return navigate("/buy-credits");
      }
    } else if ((user?.credits ?? 0) < 1) {
      toast.error("You're out of credits.");
      return navigate("/buy-credits");
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append("exam_type", examType);
    fd.append("subject", subject);
    fd.append("max_marks", maxMarks || "10");
    fd.append("word_limit", wordLimit);
    fd.append("language", language);
    files.forEach((f) => fd.append("files", f.file));

    try {
      const { data } = await api.post("/evaluations", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser((u) => (u ? { ...u, credits: data.credits } : u));
      navigate(`/result/${data.evaluation_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDF9]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 py-10">
        <span className="text-xs uppercase tracking-[0.2em] text-[#2D6A4F] font-semibold">New evaluation</span>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Submit your answer</h1>
        <p className="text-[#525252] mt-1" data-testid="credit-policy">1 credit covers up to {cfg.pages_per_credit} pages · max {cfg.max_pages} pages per answer. You have {user?.credits ?? 0} credits.</p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: details */}
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 space-y-5">
            <div>
              <Label>Exam type</Label>
              <Select value={examType} onValueChange={setExamType}>
                <SelectTrigger data-testid="exam-type-select" className="mt-1.5 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upsc">UPSC Civil Services Mains</SelectItem>
                  <SelectItem value="state_psc">State PSC Mains</SelectItem>
                  <SelectItem value="generic">Generic / Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subject">Subject / Paper <span className="text-[#525252] font-normal">(optional)</span></Label>
              <Input id="subject" data-testid="subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="e.g. GS Paper II, Polity" />
            </div>
            <div>
              <Label>Answer &amp; feedback language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="language-select" className="mt-1.5 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">English</SelectItem>
                  <SelectItem value="hindi">हिंदी (Hindi)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-[#525252]">
                {language === "hindi"
                  ? "आपका उत्तर हिंदी में पढ़ा जाएगा और पूरा मूल्यांकन हिंदी में मिलेगा।"
                  : "Choose Hindi if the answer is written in Devanagari — the full evaluation will be in Hindi."}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="marks">Max marks</Label>
                <Input id="marks" data-testid="max-marks-input" type="number" min="1" value={maxMarks} onChange={(e) => setMaxMarks(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="words">Word limit</Label>
                <Input id="words" data-testid="word-limit-input" value={wordLimit} onChange={(e) => setWordLimit(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="150" />
              </div>
            </div>
            <div className="rounded-xl bg-[#EAF1ED] border border-[#2D6A4F]/20 p-4 flex gap-3">
              <ScanText className="w-5 h-5 text-[#2D6A4F] shrink-0 mt-0.5" />
              <div className="text-sm text-[#1C1C1C]">
                <p className="font-semibold" data-testid="question-note">No need to type the question</p>
                <p className="text-[#525252] mt-0.5">Write the question at the <b>top of the first page</b> of your answer — the AI mentor will read it from your handwriting itself. If it's missing, the AI will infer it from your answer.</p>
              </div>
            </div>
          </div>

          {/* Right: upload */}
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 flex flex-col">
            <Label className="mb-2">Handwritten answer <span className="text-[#525252] font-normal">(images or PDF · up to 10 pages)</span></Label>
            <input
              ref={fileInput}
              data-testid="file-input"
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
            />
            {files.length > 0 ? (
              <div className="flex-1 flex flex-col gap-3">
                <div className="grid grid-cols-3 gap-3">
                  {files.map((f, idx) => (
                    <div key={idx} data-testid={`file-thumb-${idx}`} className="relative aspect-[3/4] rounded-xl border border-[#E5E5E5] bg-[#FAFAF5] overflow-hidden">
                      {f.isPdf ? (
                        <div className="w-full h-full flex flex-col items-center justify-center text-center px-2">
                          <FileText className="w-7 h-7 text-[#B0452A]" />
                          <span className="text-[11px] text-[#525252] mt-1 line-clamp-2 break-all">{f.name}</span>
                        </div>
                      ) : (
                        <img src={f.preview} alt={f.name} className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        data-testid={`remove-file-${idx}`}
                        onClick={() => removeFile(idx)}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 border border-[#E5E5E5] flex items-center justify-center hover:bg-white"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded">Page {idx + 1}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="add-more-btn"
                  onClick={() => fileInput.current?.click()}
                  className="rounded-xl border-2 border-dashed border-[#D6D6CE] py-3 text-sm font-medium text-[#2D6A4F] hover:border-[#2D6A4F] hover:bg-[#F7FAF8] transition-colors flex items-center justify-center gap-2"
                >
                  <UploadCloud className="w-4 h-4" /> Add more pages
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="upload-dropzone"
                onClick={() => fileInput.current?.click()}
                className="flex-1 min-h-[280px] rounded-xl border-2 border-dashed border-[#D6D6CE] flex flex-col items-center justify-center gap-3 text-center px-6 hover:border-[#2D6A4F] hover:bg-[#F7FAF8] transition-colors"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#EAF1ED] flex items-center justify-center">
                  <UploadCloud className="w-6 h-6 text-[#2D6A4F]" />
                </div>
                <div>
                  <p className="font-semibold">Upload your answer pages</p>
                  <p className="text-sm text-[#525252] mt-1">Photos (JPG/PNG) or a PDF · long answers welcome</p>
                </div>
              </button>
            )}
          </div>

          <div className="lg:col-span-2 flex items-center justify-between gap-4 bg-white rounded-2xl border border-[#E5E5E5] p-4">
            <div className="flex items-center gap-2 text-sm text-[#525252]">
              <ScanText className="w-4 h-4 text-[#2D6A4F]" />
              {files.length === 0
                ? `Write the question at the top of your first page — the AI mentor reads it along with your answer.`
                : imagesOnly
                ? `${files.length} page${files.length > 1 ? "s" : ""} · this will use ${estCredits} credit${estCredits > 1 ? "s" : ""}.`
                : `Credits depend on total pages (1 credit / ${cfg.pages_per_credit} pages, max ${cfg.max_pages}).`}
            </div>
            <Button
              data-testid="submit-evaluation-btn"
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white h-12 px-7 shrink-0"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Evaluating…</>
              ) : (
                <><Coins className="w-4 h-4 mr-2" /> {imagesOnly ? `Evaluate (${estCredits} credit${estCredits > 1 ? "s" : ""})` : "Evaluate"}</>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
